// Bölümün rapor başlığı eş anlamlıları — sections.mappingHints (Task 10, 7A).
// Geçmiş Rapor Aktarımı harita fazı bu adlardan bölümü bulur. Ekleme/silme ANINDA
// patchSection'a gider (debounce yok); gösterge çiplerle ortaktır.
// Bölüm çiplerinin ALTINDA, ikincil alan olarak yerleşir.
import { useEffect, useId, useRef, useState } from "react";
import {
  MAPPING_HINT_MAX,
  PERIOD_COPY,
  fillTemplate,
  prepareMappingHints,
} from "../../lib/period-ui";

interface MappingHintsInputProps {
  value: readonly string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}

type Notice = { text: string; tone: "warning" | "status" } | null;

export function MappingHintsInput({
  value,
  disabled = false,
  onChange,
}: MappingHintsInputProps): JSX.Element {
  const inputId = useId();
  const helpId = `${inputId}-help`;
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState<Notice>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
    },
    [],
  );

  const atLimit = value.length >= MAPPING_HINT_MAX;

  function flash(text: string, tone: "warning" | "status", ms = 1500) {
    setNotice({ text, tone });
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), ms);
  }

  function addFrom(raw: string) {
    if (disabled) return;
    const res = prepareMappingHints(value, raw);
    if (res.accepted.length > 0) {
      onChange([...value, ...res.accepted]);
      setDraft("");
    }
    // Uyarı, sayım duyurusundan önceliklidir (kesilen/atlanan başlık sessiz kalmasın);
    // uyarı 1,5 sn amber satırda, sayım 2,5 sn nötr satırda görünür.
    if (res.truncated) flash(PERIOD_COPY.hintsTooLong, "warning");
    else if (res.duplicate) flash(PERIOD_COPY.hintsDuplicate, "warning");
    else if (res.limitReached) flash(PERIOD_COPY.hintsLimit, "warning");
    else if (res.accepted.length > 0) {
      // Yapıştırma birden çok başlık ürettiğinde ekran okuyucuya duyur.
      flash(fillTemplate(PERIOD_COPY.hintsAdded, { n: res.accepted.length }), "status", 2500);
    }
  }

  function onRemove(hint: string) {
    if (disabled) return;
    onChange(value.filter((h) => h !== hint));
  }

  return (
    <div>
      <label
        htmlFor={inputId}
        style={{
          display: "block",
          fontSize: "0.75rem",
          color: "var(--color-text-3)",
          marginBottom: 4,
        }}
      >
        {PERIOD_COPY.hintsLabel}{" "}
        <span style={{ color: "var(--color-text-3)", opacity: 0.8 }}>
          {fillTemplate(PERIOD_COPY.hintsCount, { n: value.length })}
        </span>
      </label>
      <p id={helpId} style={{ fontSize: "0.72rem", color: "var(--color-text-3)", marginBottom: 6 }}>
        {PERIOD_COPY.hintsHelp}
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          id={inputId}
          type="text"
          value={draft}
          aria-describedby={helpId}
          disabled={disabled || atLimit}
          placeholder={PERIOD_COPY.hintsPlaceholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addFrom(draft);
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            // Virgül/satır sonu içeren yapıştırma anında bölünür (7A).
            if (/[,\n\r]/.test(text)) {
              e.preventDefault();
              addFrom(text);
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            padding: "8px 12px",
            borderRadius: 6,
            border: "1px solid var(--color-border)",
            background: "var(--color-bg)",
            fontSize: "0.9rem",
            fontFamily: "var(--font-sans)",
            color: "var(--color-text)",
          }}
        />
        <button
          type="button"
          disabled={disabled || atLimit || draft.trim().length === 0}
          onClick={() => addFrom(draft)}
          style={{
            padding: "8px 14px",
            fontSize: "0.8rem",
            borderRadius: 6,
            border: "1px solid var(--color-border)",
            background: "transparent",
            color: "var(--color-primary)",
            cursor: disabled || atLimit ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            opacity: disabled || atLimit || draft.trim().length === 0 ? 0.6 : 1,
          }}
        >
          {PERIOD_COPY.hintsAddButton}
        </button>
      </div>
      {atLimit ? (
        <p style={{ marginTop: 6, fontSize: "0.72rem", color: "var(--color-text-3)" }}>
          {PERIOD_COPY.hintsLimit}
        </p>
      ) : null}
      {notice ? (
        <p
          role="status"
          style={{
            marginTop: 6,
            fontSize: "0.75rem",
            display: "inline-block",
            borderRadius: 6,
            padding: "4px 8px",
            color: notice.tone === "warning" ? "var(--color-warning-text)" : "var(--color-text-2)",
            background: notice.tone === "warning" ? "var(--color-warning-bg)" : "transparent",
            border:
              notice.tone === "warning"
                ? "1px solid var(--color-warning-border)"
                : "1px solid transparent",
          }}
        >
          {notice.text}
        </p>
      ) : null}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
        {value.length === 0 ? (
          <span style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
            {PERIOD_COPY.hintsEmpty}
          </span>
        ) : (
          value.map((hint) => (
            <span
              key={hint}
              title={hint}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                maxWidth: 260,
                padding: "3px 8px",
                borderRadius: 999,
                fontSize: "0.75rem",
                color: "var(--color-text-2)",
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {hint}
              </span>
              {disabled ? null : (
                <button
                  type="button"
                  aria-label={fillTemplate(PERIOD_COPY.hintsRemoveAria, { label: hint })}
                  onClick={() => onRemove(hint)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "var(--color-text-3)",
                    cursor: "pointer",
                    lineHeight: 1,
                    padding: 0,
                    fontSize: "0.85rem",
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))
        )}
      </div>
    </div>
  );
}
